import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useSnackbar } from "notistack";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box, Stack } from "@mui/system";
import { MenuItem, TextField, Typography } from "@mui/material";
import Holder from "../../Components/HOC/Holder";
import CustomBtn from "../../Components/Reusable/CustomBtn";
import DesBtn from "../../Components/Reusable/DesBtn";
import LoadBtn from "../../Components/Reusable/LoadBtn";
import TableReusable from "../../Components/Reusable/TableReusable";
import SelectCustom from "../../Components/Inputs/SelectCustom";
import WorkspaceTabs from "../../Components/Workspace/WorkspaceTabs";
import { activeWorkspaceState, authState, popupState } from "../../Recoil/RecoilState";
import {
  baseURL,
  workspaceMembersColumns,
  workspaceRolesArr,
} from "../../utils/StaticVariables";

export const errorDetail = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail[0]?.msg || fallback;
  return typeof detail === "string" ? detail : fallback;
};

// Add a member to the current workspace.
export const AddMemberForm = ({ users = [], onSubmit }) => {
  const setPopup = useSetRecoilState(popupState);
  const formik = useFormik({
    initialValues: {
      user_id: "",
      role: "member",
    },
    validationSchema: Yup.object({
      user_id: Yup.string().required("User is required"),
      role: Yup.string().oneOf(workspaceRolesArr).required(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await onSubmit(values);
        setPopup((p) => ({ ...p, isOpen: false }));
      } catch {
        // Handled in caller
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      <Stack spacing={2}>
        {users.length > 0 ? (
          <SelectCustom
            formik={formik}
            arr={users.map((u) => ({
              val: u.user_id || u.id,
              label: `${u.username} (${u.email})`,
            }))}
            name="user_id"
            label="User"
          />
        ) : (
          <TextField
            fullWidth
            size="small"
            label="User ID"
            name="user_id"
            value={formik.values.user_id}
            onChange={formik.handleChange}
            error={formik.submitCount > 0 && Boolean(formik.errors.user_id)}
            helperText={formik.submitCount > 0 && formik.errors.user_id}
          />
        )}
        <SelectCustom formik={formik} arr={workspaceRolesArr} name="role" label="Role" />
      </Stack>
      {formik.errors.user_id && formik.submitCount > 0 && (
        <Typography color="error" variant="body2" mt={1}>
          {formik.errors.user_id}
        </Typography>
      )}
      <Stack direction="row" justifyContent="flex-end" mt={2}>
        <LoadBtn loading={formik.isSubmitting} text="Add Member" />
      </Stack>
    </Box>
  );
};

const WorkspaceMembers = () => {
  const { token } = useRecoilValue(authState);
  const activeWorkspace = useRecoilValue(activeWorkspaceState);
  const { enqueueSnackbar } = useSnackbar();
  const setPopup = useSetRecoilState(popupState);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const workspaceId = activeWorkspace?.workspace_id;

  const reportError = useCallback(
    (error, fallback) => enqueueSnackbar(errorDetail(error, fallback), { variant: "error" }),
    [enqueueSnackbar]
  );

  const fetchMembers = useCallback(async () => {
    if (!workspaceId || !token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${baseURL}workspaces/${workspaceId}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(data || []);
    } catch (error) {
      reportError(error, "Failed to load workspace members");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, token, reportError]);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      // Try admin/all-users first, fallback to users endpoint
      let res;
      try {
        res = await axios.get(`${baseURL}admin/all-users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        res = await axios.get(`${baseURL}users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setUsers(res.data || []);
    } catch {
      // If user listing is restricted, manual user_id entry is used
      setUsers([]);
    }
  }, [token]);


  useEffect(() => {
    fetchMembers();
    fetchUsers();
  }, [fetchMembers, fetchUsers]);

  const handleAddMember = async ({ user_id, role }) => {
    try {
      await axios.post(`${baseURL}workspaces/${workspaceId}/members`, { user_id, role }, auth);
      enqueueSnackbar("Member added successfully", { variant: "success" });
      fetchMembers();
    } catch (error) {
      reportError(error, "Failed to add member");
      throw error;
    }
  };

  const openAddMemberPopup = () => {
    const memberIds = new Set(members.map((m) => m.user_id || m.id));
    const availableUsers = users.filter((u) => !memberIds.has(u.user_id || u.id));

    setPopup({
      isOpen: true,
      title: `Add Member to ${activeWorkspace?.name || "Workspace"}`,
      content: <AddMemberForm users={availableUsers} onSubmit={handleAddMember} />,
      sendReq: null,
    });
  };

  const handleRoleChange = async (member, role) => {
    if (role === member.role) return;
    const memberId = member.user_id || member.id;
    try {
      await axios.put(`${baseURL}workspaces/${workspaceId}/members/${memberId}`, { role }, auth);
      enqueueSnackbar(`${member.username} role updated to ${role}`, { variant: "success" });
      fetchMembers();
    } catch (error) {
      reportError(error, "Failed to update member role");
    }
  };

  const handleRemoveMember = async (member) => {
    const memberId = member.user_id || member.id;
    try {
      await axios.delete(`${baseURL}workspaces/${workspaceId}/members/${memberId}`, auth);
      enqueueSnackbar(`${member.username} removed from workspace`, { variant: "success" });
      fetchMembers();
    } catch (error) {
      reportError(error, "Failed to remove member");
    }
  };

  const openRemovePopup = (member) => {
    setPopup({
      isOpen: true,
      title: "Remove Member",
      content: `Remove ${member.username} from ${activeWorkspace?.name || "this workspace"}? They will lose access to its cameras and data immediately.`,
      sendReq: () => handleRemoveMember(member),
    });
  };

  const tableData = members.map((m) => ({
    ...m,
    id: m.user_id || m.id,
    role: (
      <TextField
        select
        size="small"
        value={m.role}
        onChange={(e) => handleRoleChange(m, e.target.value)}
        sx={{ minWidth: { xs: 95, sm: 120 } }}
      >
        {workspaceRolesArr.map((r) => (
          <MenuItem key={r} value={r}>
            {r}
          </MenuItem>
        ))}
      </TextField>
    ),
    actions: (
      <DesBtn text="Remove from workspace" handle={() => openRemovePopup(m)}>
        Remove
      </DesBtn>
    ),
  }));

  return (
    <>
      <WorkspaceTabs />
      <Holder
        title={`Workspace Members — ${activeWorkspace?.name || ""}`}
        action={
          <CustomBtn disable={!workspaceId} handle={openAddMemberPopup}>
            Add Member
          </CustomBtn>
        }
      >
        <Box my={2} sx={{ width: "100%", overflowX: "auto" }}>
          <TableReusable data={tableData} columns={workspaceMembersColumns} loading={loading} />
        </Box>
      </Holder>
    </>
  );
};


export default WorkspaceMembers;
